/* eslint-disable max-len */
import checked from 'assets/images/checked.svg';
import close from 'assets/images/formClose.svg';
import { urls } from 'config/urls';
import useForm from 'hooks/useForm';
import { useTurnstile } from 'hooks/useTurnstile';
import { t } from 'i18next';
import { FormDataTypes, FormTypes, LeadPayload } from 'interfaces/form.interface';
import React, { useState } from 'react';
/** @jsxImportSource @emotion/react */
import tw from 'twin.macro';
import { isNotEmptyObject } from 'utils/utilities';

const Form = ({ onValidated, status, handleToggleState }: FormTypes) => {
  const { containerRef, getToken, reset } = useTurnstile();
  const [verificationFailed, setVerificationFailed] = useState(false);

  const handleValidated = async (payload: Omit<LeadPayload, 'recaptchaToken'>) => {
    setVerificationFailed(false);
    let recaptchaToken = '';
    try {
      recaptchaToken = await getToken();
    } catch {
      recaptchaToken = '';
    }
    if (!recaptchaToken && !urls.TURNSTILE_DISABLED) {
      reset();
      setVerificationFailed(true);
      return;
    }
    onValidated({ ...payload, recaptchaToken });
  };

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    handleReset,
    handleSubmit,
  } = useForm(
    {
      companyEmail: '',
      companyName: '',
      firstName: '',
      honeypot: '',
      joinMailingList: false,
      lastName: '',
      message: '',
    },
    handleValidated,
  );

  const handleClose = () => {
    handleToggleState('isRequestDataFormVisible');
    handleReset();
  };

  const shownError = (field: keyof FormDataTypes) =>
    touched[field] ? errors[field] : undefined;

  const inputCss = (value: string, error?: string) => [
    tw`border border-black rounded-[2.25rem] p-[0.5rem 1.5rem] w-full outline-none focus:border-blue-400`,
    value.trim() !== '' && !error && tw`border-blue-400`,
    error && tw`border-red-500 focus:border-red-500`,
  ];

  const fieldLabels: Partial<Record<keyof FormDataTypes, string>> = {
    companyEmail: 'form_label_company_email',
    companyName: 'form_label_company_name',
    firstName: 'form_label_first_name',
    lastName: 'form_label_surname',
    message: 'form_label_message',
  };

  const errorMessage = (field: keyof FormDataTypes) => {
    const error = shownError(field);
    if (!error) return '';
    if (error === 'form_error_required') {
      return t('form_error_required', { field: t(fieldLabels[field] as string) });
    }
    return t(error);
  };

  const errorSlot = (field: keyof FormDataTypes) => (
    <p tw="h-[1.5rem] pt-[0.25rem] ml-[1.5rem] text-left text-xs text-red-500">
      {errorMessage(field)}
    </p>
  );

  const isValid = !isNotEmptyObject(errors);

  return (
    <>
      <button onClick={handleClose} tw="absolute right-[6%]">
        <img src={close} alt="Close" />
      </button>
      <h2 tw="text-[#3A7CC5] font-medium text-xxl p-[2rem 0 1rem]">
        {t('form_interest')}
      </h2>
      <p tw="max-w-[26.688rem] m-auto text-[#898989] text-regular pb-[1.5rem]">
        {t('form_info')}
      </p>
      <form noValidate onSubmit={(e) => handleSubmit(e)} tw="text-sm">
        <div tw="flex sm:flex-col sm:gap-[1.5rem] items-start justify-between gap-[2rem]">
          <div tw="w-full">
            <input
              css={inputCss(formData.firstName, shownError('firstName'))}
              type="text"
              placeholder={t('form_first_name') as string}
              name="firstName"
              value={formData.firstName}
              onChange={(e) => handleInputChange(e.target)}
              onBlur={(e) => handleBlur(e.target)}
            />
            {errorSlot('firstName')}
          </div>
          <div tw="w-full">
            <input
              css={inputCss(formData.lastName, shownError('lastName'))}
              type="text"
              placeholder={t('form_surname') as string}
              name="lastName"
              value={formData.lastName}
              onChange={(e) => handleInputChange(e.target)}
              onBlur={(e) => handleBlur(e.target)}
            />
            {errorSlot('lastName')}
          </div>
        </div>
        <div>
          <input
            css={inputCss(formData.companyEmail, shownError('companyEmail'))}
            type="email"
            placeholder={t('form_company_email') as string}
            name="companyEmail"
            value={formData.companyEmail}
            onChange={(e) => handleInputChange(e.target)}
            onBlur={(e) => handleBlur(e.target)}
          />
          {errorSlot('companyEmail')}
        </div>
        <div>
          <input
            css={inputCss(formData.companyName, shownError('companyName'))}
            type="text"
            placeholder={t('form_company_name') as string}
            name="companyName"
            value={formData.companyName}
            onChange={(e) => handleInputChange(e.target)}
            onBlur={(e) => handleBlur(e.target)}
          />
          {errorSlot('companyName')}
        </div>
        <div>
          <textarea
            css={inputCss(formData.message, shownError('message'))}
            placeholder={t('form_message') as string}
            name="message"
            value={formData.message}
            onChange={(e) => handleInputChange(e.target)}
            onBlur={(e) => handleBlur(e.target)}
          ></textarea>
          {errorSlot('message')}
        </div>
        <input
          type="text"
          name="honeypot"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          tw="absolute left-[-9999px] w-px h-px opacity-0"
          value={formData.honeypot}
          onChange={(e) => handleInputChange(e.target)}
        />
        <div tw="flex">
          <input
            id="joinList"
            tw="mr-[0.5rem] w-[1.5rem] h-[1.5rem]"
            type="checkbox"
            name="joinMailingList"
            checked={formData.joinMailingList}
            onChange={(e) => handleInputChange(e.target)}
          />
          <label htmlFor="joinList">{t('form_agreements')}</label>
        </div>
        <div ref={containerRef} tw="mt-[1rem]" />
        {verificationFailed && (
          <p tw="pt-[0.25rem] text-left text-xs text-red-500">
            {t('form_error_verification')}
          </p>
        )}
        <button
          type="submit"
          css={[
            tw`bg-[#D7D7D7] border border-[#D7D7D7] p-[0.75rem] text-[#5C5C5C] w-full text-center rounded-[3.813rem] mb-[0.5rem] mt-[1.5rem]`,
            isValid &&
              tw`bg-blue-400 text-white border border-blue-400 hover:bg-blue-100 hover:text-gray-500`,
          ]}
        >
          {t('request_data')}
        </button>
      </form>
      <p tw="max-w-[28rem] m-auto">
        {t('form_privacy')}{' '}
        <a href="privacy-policy.pdf" target="_blank" rel="noopener noreferrer" tw="text-blue-400 underline">
          {t('form_privacy_link')}
        </a>
        .
      </p>
      {status === 'success' && isNotEmptyObject(formData) && (
        <div tw="absolute left-0 top-0 bottom-0 right-0 flex flex-col items-center justify-between bg-white rounded-[0.5rem] text-center p-[2rem 2.5rem 1.5rem 2.5rem]">
          <img alt="Checked" src={checked} tw="m-[4rem auto 2rem]" />
          <h2 tw="text-blue-400 font-medium text-xxl mb-[1.5rem]">
            {t('form_received')}
          </h2>
          <p tw="max-w-[33.188rem] w-full text-[#383838] mb-[3rem]">
            {t('form_received_message')}
          </p>
          <button
            onClick={handleClose}
            tw="max-w-[11.625rem] w-full rounded-[3.813rem] mb-[3rem] p-[0.5rem 0.75rem] text-white bg-blue-400 hover:bg-blue-100 hover:text-gray-500"
          >
            {t('form_close')}
          </button>
        </div>
      )}
      {status === 'error' && isNotEmptyObject(formData) && (
        <div tw="absolute left-0 top-0 bottom-0 right-0 flex flex-col items-center justify-between bg-white rounded-[0.5rem] text-center p-[2rem 2.5rem 1.5rem 2.5rem]">
          <img alt="Checked" src={checked} tw="m-[4rem auto 2rem]" />
          <h2 tw="text-blue-400 font-medium text-xxl mb-[1.5rem]">
            {t('form_error')}
          </h2>
          <button
            onClick={handleClose}
            tw="max-w-[11.625rem] w-full rounded-[3.813rem] mb-[3rem] p-[0.5rem 0.75rem] text-white bg-blue-400 hover:bg-blue-100 hover:text-gray-500"
          >
            {t('form_close')}
          </button>
        </div>
      )}
    </>
  );
};

export default Form;
